'use client';

import { createContext, type CSSProperties, type PropsWithChildren, type ReactNode, useContext, useState } from 'react';

type SetCouponScrollbarHeight = (height: number) => void;

const couponScrollbarHeightContext = createContext<SetCouponScrollbarHeight>(() => undefined);

type CouponScrollbarHeightProviderProps = PropsWithChildren & {
  className?: string;
};

/**
 * Hook that returns a setter for the measured height of the coupon scroll area scrollbar.
 * The height is 0 when the browser uses overlay scrollbars or when nothing overflows.
 *
 * @returns setter for the scrollbar height in pixels
 */
export function useSetCouponScrollbarHeight(): SetCouponScrollbarHeight {
  return useContext(couponScrollbarHeightContext);
}

/**
 * Provider that exposes the measured coupon scrollbar height as the `--coupon-scrollbar-height`
 * CSS variable, so the event column can reserve the same space as the scrolling coupon column.
 *
 * @param root0 props
 * @param root0.children children components
 * @param root0.className class name for the wrapping element
 * @returns provider element carrying the scrollbar height CSS variable
 */
export function CouponScrollbarHeightProvider({ children, className }: CouponScrollbarHeightProviderProps): ReactNode {
  const [scrollbarHeight, setScrollbarHeight] = useState(0);

  return (
    <couponScrollbarHeightContext.Provider value={setScrollbarHeight}>
      <div className={className} style={{ '--coupon-scrollbar-height': `${scrollbarHeight}px` } as CSSProperties}>
        {children}
      </div>
    </couponScrollbarHeightContext.Provider>
  );
}
